import { useEffect } from 'react';
import { useTrellusHostStore } from './trellus-host-store';
import { enqueueUiWrite, isAppearanceFieldAdvertised, writeComponentConfiguration } from './ui-configure';

/**
 * Hides Trellus's floating launcher pill while Clay's own headset button is on
 * screen, and gives it back the moment that button goes away.
 *
 * v15 contract: `ui.configure` component `launcher` appearance
 * `{ visible: false }` is honored ONLY while `<html data-trellus-clay-launcher-host>`
 * is present. So the attribute is the safety: whatever happens to the
 * `ui.configure` writes (failure, extension reload, a lost race), removing the
 * attribute — which cleanup does synchronously — restores the pill. The
 * `visible:true` write on cleanup is belt and braces so the document preference
 * does not linger.
 */

export const TRELLUS_LAUNCHER_HOST_ATTRIBUTE = 'data-trellus-clay-launcher-host';
const LAUNCHER = 'launcher';

/** Is `components.launcher.appearance.visible` advertised as a boolean (exact v15 capability)? */
export function isLauncherHideAdvertised(): boolean {
  return isAppearanceFieldAdvertised(LAUNCHER, 'visible', 'boolean');
}

const writeLauncherVisible = (visible: boolean) => writeComponentConfiguration(LAUNCHER, { command: 'ui.configure', appearance: { visible } });

/**
 * While `active` (the headset button is mounted AND visible), keep the launcher
 * pill hidden. StrictMode: all per-run state is local to the effect run (no
 * refs), so each setup starts from a clean slate and its cleanup undoes
 * exactly what that run did.
 */
export function useTrellusLauncherHidden(active: boolean): void {
  const nonce = useTrellusHostStore(s => s.nonce);
  useEffect(() => {
    if (!active || !isLauncherHideAdvertised()) return;
    let cancelled = false;
    let sentHide = false;
    const root = document.documentElement;
    root.setAttribute(TRELLUS_LAUNCHER_HOST_ATTRIBUTE, '');
    enqueueUiWrite(async () => {
      if (cancelled) return;
      sentHide = await writeLauncherVisible(false);
    });
    return () => {
      cancelled = true;
      root.removeAttribute(TRELLUS_LAUNCHER_HOST_ATTRIBUTE);
      enqueueUiWrite(async () => {
        if (sentHide) await writeLauncherVisible(true);
      });
    };
  }, [active, nonce]);
}
